import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useDependencies } from "./useDependencies";
import { useAuth } from "./useAuth";
import { useDocumentationArea } from "./useDocumentationArea";
import { ActorBasicData } from "src/contexts/_shared/domain/ActorBasicData";
import { DocumentationAreaNotFoundError } from "src/contexts/documentationArea/domain/errors/DocumentationAreaNotFoundError";
import { ActorId } from "src/contexts/documentationArea/domain/ActorId";

type UpdateBasicDataParams = {
  actorId: ActorId;
  basicData: ActorBasicData;
};

export const useUpdateBasicData = () => {
  const queryClient = useQueryClient();
  const { token } = useAuth();
  const { documentationArea } = useDocumentationArea();
  const { documentationAreaController } = useDependencies();

  if (!documentationArea) {
    throw new DocumentationAreaNotFoundError();
  }

  const mutationFn = async ({ actorId, basicData }: UpdateBasicDataParams) => {
    return await documentationAreaController.updateBasicData(
      token,
      documentationArea.getId(),
      actorId,
      basicData,
    );
  };

  const { mutate, isPending, isSuccess, error } = useMutation({
    mutationFn,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["DocumentationArea", documentationArea.getId()] });
      queryClient.invalidateQueries({
        queryKey: ["DocumentationAreaActorBasicData", documentationArea.getId(), variables.actorId],
      });
    },
  });

  return { isPending, mutate, isSuccess, error };
};
